import { faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { narrativeDisable } from '../../../../api/actions/narrative/narrative-disable';
import { narrativeEnable } from '../../../../api/actions/narrative/narrative-enable';
import { getAllNarratives } from '../../../../api/actions/narrative/narratives-get-all';
import { getAllVideoServices } from '../../../../api/actions/video-service/video-service-get-all';
import { Button } from '../../../../components/shared/button/button';
import { LinkButton } from '../../../../components/shared/link-button/link-button';
import { Select } from '../../../../components/shared/select/select';
import { Loader } from '../../../../components/shared/loader/loader';
import { CurrentUserContext } from '../../../../components/user/current-user-manager';
import { IExternalReference } from '../../../../models/external-reference';
import { basePerformError } from '../../../../helpers/error-helpers';
import { joinPath } from '../../../../helpers/path-helpers';
import {
  INarrative,
  NarrativeAvailabiltyFilter,
} from '../../../../models/narrative';
import { IVideoService } from '../../../../models/video-service';
import { compareText } from '../../../../helpers/array-helpers';
import styles from './narratives-list.module.scss';
import { getAllNarrativesShallow } from '../../../../api/actions/narrative/narratives-get-all-shallow';

export function NarrativeList(props: RouteComponentProps) {
  const [loading, setLoading] = useState(true);
  const [narratives, setNarratives] = useState<INarrative[]>([]);
  const [allNarrativesShallow, setAllNarrativesShallow] = useState<INarrative[]>([]);
  const [videoServices, setVideoServices] = useState<IVideoService[]>([]);
  const [filterServiceUid, setFilterServiceUid] = useState<string | undefined>(
    undefined
  );
  const [filterAvailability, setFilterAvailability] = useState<
    string | undefined
  >(undefined);
  const [filterNarrativeTitle, setFilterNarrativeTitle] = useState<string | undefined>(undefined);

  const currentUserContext = React.useContext(CurrentUserContext);
  const loadData = React.useCallback(async (serviceIdentifier: string | undefined) => {
    if (!currentUserContext?.currentUser) return;
    //if(loading) return;

    setLoading(true);
    
    try {
      const services = await getAllVideoServices();
      const allVideoService: IVideoService = {
        uid: 'all',
        identifier: 'all',
        name: 'All',
        url: '',
        available: true,
        comingSoon: false,
        platforms: []
      };
      services.unshift(allVideoService);
      setVideoServices(
        services?.sort((el1, el2) =>
          compareText(el1, el2, (el) => el.name)
        )
      );

      //console.log('serviceIdentifier in loadData: ', serviceIdentifier);
      const nrs = await getAllNarratives(false, undefined, serviceIdentifier);
      setNarratives(
        nrs.filter(
          (n) =>
            currentUserContext?.isAdmin() ||
            currentUserContext.getNarratives()?.some((n1) => n1.uid === n.uid)
        )
      );

      const nrsShallow = await getAllNarrativesShallow(false, undefined, undefined);
      setAllNarrativesShallow(nrsShallow);

    } catch (err) {
      basePerformError(err, props.history);
    }
    setLoading(false);
  }, [props.history, currentUserContext?.currentUser]);

  const getServiceIdentifier = () => {
    let serviceIdentifier: string | undefined = undefined;
    const params = new URLSearchParams(props.location.search);
    const serviceParam = params.get('service');
    if(serviceParam) {
      serviceIdentifier = serviceParam;
    } else {
      serviceIdentifier = "none";
    }
    return serviceIdentifier;
  }

  useEffect(() => {
    
    const serviceParam = getServiceIdentifier();
    if(serviceParam) {
      const service = videoServices.find((s) => s.identifier === serviceParam);
      if(service) {
        //console.log('found service in useEffect with change of video service or location: ', service.name);
        setFilterServiceUid(service.uid);
      }
    }
  }, [videoServices]);


  useEffect(() => {    

    loadData(getServiceIdentifier());
  }, [loadData, currentUserContext?.currentUser]);

  const renderButtons = () => (
    <div className={styles.buttons}>
      <Button className="orange" to={joinPath(props.match.url, '_')}>
        Add narrative
      </Button>
    </div>
  );

  const disableNarraive = async (uid: string) => {
    try {
      await narrativeDisable(uid);

      loadData(getServiceIdentifier());
      
      toast.success('Item has been successfully saved');
    } catch (err) {
      basePerformError(err, props.history);
    }
  };

  const getServiceList = (extServices: IExternalReference[] | undefined) => {
    let serviceList = '';
    if (!extServices) return serviceList;

    let i = 0;
    while (i < extServices.length) {
      if (extServices[i].enabled) {
      serviceList =
        serviceList.length > 0
            ? serviceList + ', ' + extServices[i].serviceName.toUpperCase()
            : extServices[i].serviceName.toUpperCase();
      }
      i = i + 1;
    }
    return serviceList;
  };

  const handleSearchNarrativeByTitle = (title: string | undefined) => {
    console.log('handleSearchNarrativeByTitle: ', title);
    setFilterNarrativeTitle(title);
    props.history.push(joinPath(props.match.url, title));
  };

  const handleServiceChange = (serviceUid: string | undefined) => {
    console.log('handleServiceChange: ', serviceUid);
    let serviceIdentifier: string | undefined = undefined;
    if(serviceUid) {
      setFilterServiceUid(serviceUid);
      const service = videoServices.find((s) => s.uid === serviceUid);
      if(service) {
        //console.log('found service in handleServiceChange: ', service.name);
        serviceIdentifier = service.identifier;
        props.history.push(joinPath(props.match.url, `?service=${service.identifier}`));
      }
    } else {
      setFilterServiceUid(undefined);
      serviceIdentifier = "none";
      props.history.push(joinPath(props.match.url, `?service=none`));
    }
    loadData(serviceIdentifier).catch(() =>{/** */});
  };

  const enableNarraive = async (uid: string) => {
    try {
      await narrativeEnable(uid);

      loadData(getServiceIdentifier());
      toast.success('Item has been successfully saved');
    } catch (err) {
      basePerformError(err, props.history);
    }
  };

  const getNarrativesByFilter = (
    narrs: INarrative[] | undefined = undefined
  ) => {
    let narrativesList = narrs;
    if (filterServiceUid && filterServiceUid !== 'all') {
      const service = videoServices.find((s) => s.uid === filterServiceUid);
      narrativesList = narrativesList?.filter((n) =>
        n.externalReferences?.some(
          (er) =>
            er.serviceName &&
            er.serviceName.toLowerCase() === service?.identifier.toLowerCase()
        )
      );
    }
    if (filterAvailability) {
      narrativesList = narrativesList?.filter(
        (n) => n.available === compareValue(filterAvailability)
      );
    }
    return narrativesList;
  };
  const compareValue = (value: any) => {
    return value === '1' ? true : false;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {renderButtons()}

      <div className={styles.buttonView}>
        <div className={styles.filters}>
          <div className={`${styles.filter} ${styles.filterService}`}>
            <span>Search All Narratives by Title:</span>
            <Select<INarrative, string>
              emptyTitle=""
              data={(allNarrativesShallow || [])}
              titleExtractor={(n) => n?.title}
              onChange={(e) => handleSearchNarrativeByTitle(e.target.value)}
              value={filterNarrativeTitle}
            />
          </div>
        </div>

        <div className={styles.filters}>
          <div className={`${styles.filter} ${styles.filterService}`}>
            <span>Service:</span>
            <Select<IVideoService, string>
              emptyTitle="None"
              data={(videoServices || [])}
              titleExtractor={(n) => n?.name}
              onChange={(e) => handleServiceChange(e.target.value)}
              value={filterServiceUid}
            />
          </div>
        </div>

        <div className={styles.filters}>
          <div className={`${styles.filter} ${styles.filterService}`}>
            <span>Availability:</span>
            <Select
              emptyTitle="All"
              data={NarrativeAvailabiltyFilter || []}
              titleExtractor={(n) => n?.name}
              onChange={(e) => setFilterAvailability(e.target.value)}
              value={filterAvailability}
            />
          </div>
        </div>

      </div>
      <table cellPadding="0" cellSpacing="0" className={`list ${styles.list}`}>
        <thead>
          <tr>
            <th>Title / Description</th>
            <th>Type</th>
            <th>Service</th>
            <th>Platforms</th>
            <th>Status</th>
            <th>Rating</th>
            <th>Episodes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {getNarrativesByFilter(narratives)
            ?.sort((n1, n2) =>
              n1.title > n2.title ? 1 : n1.title < n2.title ? -1 : 0
            )
            .map((n) => (
              <tr key={n.uid}>
                <td>
                  <div className={styles.titleContainer}>
                    <div
                      className={`${styles.mark} ${
                        n.available ? '' : styles.disabled
                      }`}
                    >
                      &nbsp;
                    </div>
                    <div className={styles.title}>
                      {n.title}
                      <div>{n.description}</div>
                    </div>
                  </div>
                </td>
                <td>{n.mediaType?.toUpperCase()}</td>
                <td>{getServiceList(n.externalReferences)}</td>
                <td>{n.platforms ? n.platforms.toString() : ''}</td>
                <td>{n.status?.toUpperCase()}</td>
                <td>
                  {n.minAgeRating >= 7 && n.minAgeRating < 13
                    ? `Older Kids ${n.minAgeRating}+`
                    : n.minAgeRating >= 13 && n.minAgeRating < 16
                    ? `Teens ${n.minAgeRating}+`
                    : n.minAgeRating >= 16 && n.minAgeRating < 18
                    ? `Young Adults ${n.minAgeRating}+`
                    : n.minAgeRating >= 18
                    ? `Adults ${n.minAgeRating}+`
                    : 'G'}
                </td>
                <td>
                  {!!n.episodes?.length &&
                    `${n.episodes.filter((e) => e.available).length} / ${
                      n.episodes?.length
                    }`}
                </td>
                <td>
                  <LinkButton to={joinPath(props.match.url, n.uid)}>
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </LinkButton>
                  <br />
                  {!n.available && (
                    <>
                      <LinkButton
                        className={styles.enableBtn}
                        onClick={() => enableNarraive(n.uid)}
                      >
                        <FontAwesomeIcon icon={faUnlock} /> Enable
                      </LinkButton>
                      <br />
                    </>
                  )}
                  {n.available && (
                    <>
                      <LinkButton onClick={() => disableNarraive(n.uid)}>
                        <FontAwesomeIcon icon={faLock} /> Disable
                      </LinkButton>
                      <br />
                    </>
                  )}
                  <LinkButton
                    onClick={() => {
                      /* */
                    }}
                    className="red"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} /> Delete
                  </LinkButton>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {renderButtons()}
    </>
  );
}
